import {
  useSnackbar,
  WBButton,
  WBFlex,
  WBForm,
  WBTypography,
  WBTextFieldV2,
  WBBox,
  WBSelectV2,
  WBTooltip,
  WBIconButton,
  WBLinkButton,
} from '@admiin-com/ds-web';
import { gql, useQuery } from '@apollo/client';
import { useState } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ClientInput,
  Contact,
  CreateEntityInput,
  CSGetSelectedEntityId as GET_SELECTED_ENTITY_ID,
  EntityType,
  EntityUser,
} from '@admiin-com/ds-graphql';
import {
  Checkbox,
  FormControlLabel,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import InformationIcon from '../../../../assets/icons/information-circle.svg';
import { GSTReportingPeriod } from '@admiin-com/ds-graphql';
import { useUpdateContact } from '../../../hooks/useUpdateContact/useUpdateContact';
import ErrorHandler from '../../../components/ErrorHandler/ErrorHandler';
import ContactForm from '../ContactForm/ContactForm';
import EntityCreateForm from '../EntityCreateForm/EntityCreateForm';
import { useCreateClient } from '../../hooks/useCreateClient';
import { ClientPartnerManagerForm } from './ClientPartnerManagerForm';
import { findFirstErrorField } from '../../../helpers/string';
import { EntityEntry, CreateClientForm } from './types';

interface ClientsCreateFormProps {
  selected?: EntityUser | null;
  onSubmitted?: (contact: Contact) => void;
}

const getDefaultEntityEntry = (selected?: EntityUser | null): EntityEntry => ({
  entity: {
    type: selected?.entity?.type ?? EntityType.COMPANY,
    name: selected?.entity?.name ?? '',
    taxNumber: selected?.entity?.taxNumber ?? '',
    website: selected?.entity?.website ?? '',
    gstRegistered: selected?.entity?.gstRegistered ?? false,
    gstReportingPeriod: selected?.entity?.gstRegistered
      ? selected?.entity?.gstReportingPeriod ?? null
      : null,
    address: {
      fullAddress:
        selected?.entity?.address?.fullAddress ??
        selected?.entity?.address?.address1 ??
        '',
      address1: selected?.entity?.address?.address1 ?? '',
      address2: selected?.entity?.address?.address2 ?? '',
      country: selected?.entity?.address?.country ?? '',
      countryCode: selected?.entity?.address?.countryCode ?? 'AU',
      city: selected?.entity?.address?.city ?? '',
      state: selected?.entity?.address?.state ?? '',
      stateCode: selected?.entity?.address?.stateCode ?? '',
      postalCode: selected?.entity?.address?.postalCode ?? '',
    },
  },
  clientCode: '',
  partner: undefined,
  manager: undefined,
});

export function ClientsCreateForm({
  selected = null,
  onSubmitted,
}: ClientsCreateFormProps) {
  const { t } = useTranslation();
  const methods = useForm<CreateClientForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      entities: [getDefaultEntityEntry(selected)],
      client: {
        name: `${selected?.firstName ?? ''} ${selected?.lastName ?? ''}`,
        firstName: selected?.firstName ?? '',
        lastName: selected?.lastName ?? '',
        mobile: selected?.entity?.contact?.phone ?? '',
        email: selected?.email ?? '',
        phone: selected?.entity?.contact?.phone ?? '',
      },
      taskSendSMS: selected?.taskSendSMS ?? true,
      suppressInvite: true,
      clientGroupCode: '',
    },
  });
  const { handleSubmit, control, watch, setValue } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entities',
  });

  const { createClient, error: createError } = useCreateClient();

  const [loading, setLoading] = useState(false);
  const [creationProgress, setCreationProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const showSnackbar = useSnackbar();

  const [updateContact, { error: updateError }] = useUpdateContact();

  const { data: selectedEntityIdData } = useQuery(gql(GET_SELECTED_ENTITY_ID));
  const [created, setCreated] = useState(false);

  const entityId = selectedEntityIdData?.selectedEntityId;

  const onSubmit = async (data: CreateClientForm) => {
    setLoading(true);
    try {
      const client: ClientInput = {
        ...data.client,
        mobile: data?.client?.mobile || undefined,
        name: `${data?.client?.firstName?.trim() ?? ''} ${
          data?.client?.lastName?.trim() ?? ''
        }`,
      };

      if (selected) {
        const updatedContact = await updateContact({
          variables: {
            input: {
              ...client,
              id: selected.id,
            },
          },
        });
        onSubmitted && onSubmitted(updatedContact?.data.updateContact);
      } else {
        // Create each entity as a separate client
        const totalEntities = data.entities.length;
        for (let i = 0; i < totalEntities; i++) {
          // Update progress for multiple entities
          if (totalEntities > 1) {
            setCreationProgress({ current: i + 1, total: totalEntities });
          }

          const entityEntry = data.entities[i];
          const entity: CreateEntityInput = {
            ...entityEntry.entity,
            name:
              entityEntry.entity.type === EntityType.INDIVIDUAL
                ? `${data?.client?.firstName?.trim() ?? ''} ${
                    data?.client?.lastName?.trim() ?? ''
                  }`
                : entityEntry.entity.name?.trim(),
          };

          const createdContact = await createClient({
            variables: {
              input: {
                entityId,
                client,
                entity,
                taskSendSMS: data.taskSendSMS,
                suppressInvite: data.suppressInvite,
                partnerId: entityEntry.partner?.userId,
                managerId: entityEntry.manager?.userId,
                clientCode: entityEntry.clientCode?.trim() || undefined,
                clientGroupCode: data.clientGroupCode?.trim() || undefined,
              },
            },
          });

          // Only call onSubmitted for the last entity
          if (i === data.entities.length - 1) {
            onSubmitted && onSubmitted(createdContact?.data.createContact);
          }
        }

        setCreated(true);
        setCreationProgress(null);
        showSnackbar({
          message: t(
            data.entities.length > 1 ? 'clientsCreated' : 'clientCreated',
            { ns: 'contacts' }
          ),
          severity: 'success',
          horizontal: 'right',
          vertical: 'bottom',
        });
      }

      setLoading(false);
    } catch (err) {
      console.log('error create client: ', err);
      setLoading(false);
      setCreationProgress(null);
    }
  };
  const onError = (errorFields: any) => {
    const firstErrorField = findFirstErrorField(errorFields);
    if (firstErrorField) {
      methods.setFocus(firstErrorField as any);
      const errorElement = document.getElementsByName(firstErrorField)?.[0];
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleAddEntity = () => {
    append(getDefaultEntityEntry());
  };

  const handleRemoveEntity = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };
  //@ts-ignore
  return (
    <WBFlex flexDirection="column" alignItems="center" mb={4}>
      <WBForm onSubmit={handleSubmit(onSubmit, onError)} alignSelf="stretch">
        <FormProvider {...methods}>
          <WBTypography
            variant={'h3'}
            noWrap
            component="div"
            color="dark"
            sx={{ flexGrow: 1, textAlign: 'left' }}
          >
            {t('clientDetails', { ns: 'contacts' })}
          </WBTypography>
          <ContactForm disabled={selected !== null} isClient />

          {/* Client Group Code - under Client Details section */}
          <WBBox mt={3}>
            <Controller
              name="clientGroupCode"
              control={control}
              render={({ field }) => (
                <WBTextFieldV2
                  {...field}
                  placeholder="e.g. SMI01"
                  label={t('clientGroupCode', {
                    ns: 'clients',
                    defaultValue: 'Client Group Code',
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <WBTooltip
                          title={t('clientGroupCodeTooltip', {
                            ns: 'clients',
                            defaultValue:
                              'A code to group all entities for this client. Automatically generated if blank',
                          })}
                        >
                          <WBBox
                            sx={{
                              cursor: 'help',
                              display: 'flex',
                              alignItems: 'center',
                              '& svg': { width: 20, height: 20 },
                            }}
                            component="span"
                          >
                            <InformationIcon />
                          </WBBox>
                        </WBTooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </WBBox>

          {/* Entity entries - multiple entities support */}
          {fields.map((field, index) => {
            const gstRegisteredValue = watch(
              `entities.${index}.entity.gstRegistered`
            );

            return (
              <WBBox
                key={field.id}
                mt={5}
                sx={{
                  borderTop: index > 0 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  pt: index > 0 ? 3 : 0,
                }}
              >
                <WBFlex
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <WBTypography
                    variant={'h3'}
                    noWrap
                    component="div"
                    color="dark"
                    sx={{ flexGrow: 1, textAlign: 'left' }}
                  >
                    {t('entityDetails', { ns: 'contacts' })}
                    {fields.length > 1 && ` ${index + 1}`}
                  </WBTypography>
                  {fields.length > 1 && (
                    <WBIconButton
                      icon="Close"
                      onClick={() => handleRemoveEntity(index)}
                      aria-label={t('removeEntity', {
                        ns: 'clients',
                        defaultValue: 'Remove entity',
                      })}
                    />
                  )}
                </WBFlex>

                <EntityCreateForm
                  type="CLIENT"
                  key={field.id}
                  disabled={selected !== null}
                  prefix={`entities.${index}.`}
                />

                <WBFlex>
                  <Controller
                    control={control}
                    name={`entities.${index}.entity.gstRegistered`}
                    defaultValue={false}
                    render={({ field: gstField }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={gstField.value ?? false}
                            onChange={(e, checked) => {
                              gstField.onChange(checked);
                              setValue(
                                `entities.${index}.entity.gstReportingPeriod`,
                                checked ? GSTReportingPeriod.QUARTERLY : null
                              );
                            }}
                          />
                        }
                        label={t('gstRegisteredPlaceholder', {
                          ns: 'settings',
                        })}
                      />
                    )}
                  />
                </WBFlex>
                {gstRegisteredValue && (
                  <WBFlex>
                    <Controller
                      control={control}
                      name={`entities.${index}.entity.gstReportingPeriod`}
                      defaultValue={GSTReportingPeriod.QUARTERLY}
                      render={({ field: gstPeriodField }) => (
                        <WBBox mt={3} sx={{ width: '100%' }}>
                          <WBSelectV2
                            {...gstPeriodField}
                            fullWidth
                            label={t('gstReportingPeriod', { ns: 'settings' })}
                            placeholder={t('gstReportingPeriodPlaceholder', {
                              ns: 'settings',
                            })}
                            onChange={(e) => gstPeriodField.onChange(e)}
                          >
                            {Object.values(GSTReportingPeriod).map((value) => (
                              <MenuItem key={value} value={value}>
                                {t(value, { ns: 'taskbox' })}
                              </MenuItem>
                            ))}
                          </WBSelectV2>
                        </WBBox>
                      )}
                    />
                  </WBFlex>
                )}

                {/* Client Code - under each Entity Details section */}
                <WBBox mt={3}>
                  <Controller
                    name={`entities.${index}.clientCode`}
                    control={control}
                    render={({ field: clientCodeField }) => (
                      <WBTextFieldV2
                        {...clientCodeField}
                        placeholder="e.g. SMI01ABC"
                        label={t('clientCode', {
                          ns: 'clients',
                          defaultValue: 'Client Code',
                        })}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <WBTooltip
                                title={t('clientCodeTooltip', {
                                  ns: 'clients',
                                  defaultValue:
                                    'A code to uniquely identify this client. Automatically generated if blank',
                                })}
                              >
                                <WBBox
                                  sx={{
                                    cursor: 'help',
                                    display: 'flex',
                                    alignItems: 'center',
                                    '& svg': { width: 20, height: 20 },
                                  }}
                                  component="span"
                                >
                                  <InformationIcon />
                                </WBBox>
                              </WBTooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </WBBox>

                {/* Partner/Manager selection - per entity */}
                <ClientPartnerManagerForm entityIndex={index} />
              </WBBox>
            );
          })}

          {/* Add another entity button */}
          {!selected && (
            <WBFlex justifyContent="flex-start" mt={3}>
              <WBLinkButton onClick={handleAddEntity}>
                {t('addEntity', {
                  ns: 'clients',
                  defaultValue: '+ Add another entity',
                })}
              </WBLinkButton>
            </WBFlex>
          )}

          <WBTypography
            mt={5}
            variant={'h3'}
            noWrap
            component="div"
            color="dark"
            sx={{ flexGrow: 1, textAlign: 'left' }}
          >
            {t('invitationSettings', { ns: 'contacts' })}
          </WBTypography>

          <Controller
            //@ts-ignore TODO need to review cause of error
            name="taskSendSMS"
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <FormControlLabel
                sx={{}}
                control={
                  <Checkbox
                    //@ts-ignore TODO need to review cause of error
                    checked={field.value}
                    onChange={field.onChange}
                    inputRef={field.ref}
                  />
                }
                label={t('taskSendSMS', { ns: 'clients' })}
              />
            )}
          />
        </FormProvider>

        {/* Progress indicator for multiple entity creation */}
        {loading && creationProgress && (
          <WBTypography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, textAlign: 'center' }}
          >
            {t('creatingEntityProgress', {
              ns: 'clients',
              defaultValue: 'Creating entity {{current}} of {{total}}...',
              current: creationProgress.current,
              total: creationProgress.total,
            })}
          </WBTypography>
        )}

        <WBButton
          sx={{
            mt: {
              xs: 6,
              sm: 8,
            },
          }}
          loading={loading}
        >
          {t(`${selected ? 'updateClient' : 'createClient'}`, {
            ns: 'contacts',
          })}
        </WBButton>
      </WBForm>
      <ErrorHandler errorMessage={createError?.message} />
      <ErrorHandler errorMessage={updateError?.message} />
    </WBFlex>
  );
}
